import { type Icon } from "@chakra-ui/react";
import { type StaticImageData } from "next/image";
import { LinkedinIcon, XIcon, YoutubeIcon } from "ui/icons";
import { trackChangelogClicked } from "components/event-tracking";
import PrivacyChoicesIcon from "../../../public/assets/privacy/privacy-choices.svg";

export type Group = {
  title: string;
  sub: Subgroup[];
};

export type Subgroup = {
  title?: string;
  nav: Item[];
};

export type Item = {
  title: string;
  url?: string;
  /** Used for social icons */
  leftIcon?: typeof Icon;
  /** Used for privacy choices icon */
  rightIcon?: IconImage;
  onClick?: () => void;
};

export type IconImage = {
  src: StaticImageData;
  alt: string;
};
export const config: Group[] = [
  {
    title: "Platform",
    sub: [
      {
        nav: [
          {
            title: "Composable CDP",
            url: "/platform/composable-cdp",
          },
          {
            title: "Hightouch Agents",
            url: "/platform/agents",
          },
          {
            title: "AI Decisioning",
            url: "/platform/ai-decisioning",
          },
          {
            title: "Reverse ETL",
            url: "/platform/reverse-etl",
          },
          {
            title: "Customer Studio",
            url: "/platform/customer-studio",
          },
          {
            title: "Identity Resolution",
            url: "/platform/identity-resolution",
          },
          {
            title: "Hightouch Events",
            url: "/platform/events",
          },
          {
            title: "Real-time Personalization",
            url: "/platform/real-time-personalization",
          },
          {
            title: "Match Booster",
            url: "/platform/match-booster",
          },
          {
            title: "Intelligence",
            url: "/platform/intelligence",
          },
          {
            title: "Observability",
            url: "/platform/observability",
          },
          {
            title: "Extensions",
            url: "/platform/extensions",
          },
          {
            title: "Security",
            url: "/platform/security",
          },
        ],
      },
    ],
  },
  {
    title: "Solutions",
    sub: [
      {
        title: "By team",
        nav: [
          {
            title: "Marketing",
            url: "/solutions/marketing",
          },
          {
            title: "Advertising",
            url: "/solutions/advertising",
          },
          {
            title: "Data",
            url: "/solutions/data",
          },
          {
            title: "Operations",
            url: "/solutions/operations",
          },
        ],
      },
      {
        title: "By industry",
        nav: [
          {
            title: "Retail & eCommerce",
            url: "/solutions/retail",
          },
          {
            title: "Financial Services",
            url: "/solutions/financial-services",
          },
          {
            title: "Travel & Hospitality",
            url: "/solutions/travel",
          },
          {
            title: "Media & Entertainment",
            url: "/solutions/media",
          },
          {
            title: "Quick Service Restaurants",
            url: "/solutions/qsr",
          },
          {
            title: "Healthcare",
            url: "/solutions/healthcare",
          },
          {
            title: "B2B SaaS",
            url: "/solutions/saas",
          },
        ],
      },
    ],
  },
  {
    title: "Resources",
    sub: [
      {
        nav: [
          {
            title: "Docs",
            url: "/docs",
          },
          {
            title: "Changelog",
            url: "https://changelog.hightouch.io/",
            onClick: trackChangelogClicked,
          },
          {
            title: "Integrations",
            url: "/integrations",
          },
          {
            title: "Blog",
            url: "/blog/category/all/page/1",
          },
          {
            title: "Playbooks",
            url: "/playbooks",
          },
          {
            title: "Whitepapers & guides",
            url: "/resources",
          },
          {
            title: "Compare CDPs",
            url: "/compare-cdps",
          },
          {
            title: "CDP Industries Hub",
            url: "/cdp-industries",
          },
          {
            title: "Composable CDP Hub",
            url: "/solutions/composable-cdp",
          },
          {
            title: "SQL Dictionary",
            url: "/sql-dictionary",
          },
        ],
      },
    ],
  },
  {
    title: "Company",
    sub: [
      {
        nav: [
          {
            title: "Careers",
            url: "/careers",
          },
          {
            title: "About us",
            url: "/about",
          },
          {
            title: "Customers",
            url: "/customers",
          },
          {
            title: "Pricing",
            url: "/pricing",
          },
          {
            title: "Press",
            url: "/press",
          },
          {
            title: "Events",
            url: "/events",
          },
          {
            title: "Solutions partners",
            url: "/solutions-partners",
          },
        ],
      },
      {
        title: "Social",
        nav: [
          {
            leftIcon: LinkedinIcon,
            title: "LinkedIn",
            url: "https://www.linkedin.com/company/hightouchio/",
          },
          {
            leftIcon: XIcon,
            title: "Twitter",
            url: "https://x.com/HightouchData",
          },
          {
            leftIcon: YoutubeIcon,
            title: "Youtube",
            url: "https://www.youtube.com/channel/UCmjKHswj5UPdKqkrHB8RhVw",
          },
        ],
      },
    ],
  },
  {
    title: "Legal & Privacy",
    sub: [
      {
        nav: [
          {
            title: "Privacy Policy",
            url: "/privacy-policy",
          },
          {
            title: "Cookie Policy",
            url: "/cookie-policy",
          },
          {
            title: "Terms of Service",
            url: "/terms-of-service",
          },
          {
            title: "Platform Privacy Notice",
            url: "/platform-privacy",
          },
          {
            title: "Cookie preferences",
            onClick() {
              // eslint-disable-next-line new-cap
              window?.OneTrust?.ToggleInfoDisplay?.();
            },
          },
          {
            title: "Your privacy choices",
            url: "https://preferences.hightouch.com",
            rightIcon: { src: PrivacyChoicesIcon, alt: "California Consumer Privacy Act (CCPA) Opt-Out Icon" },
          },
          // @TODO @taylor add in with approval from Nikko
          // {
          //   title: "Trust center",
          //   url: "https://trust.hightouch.com/",
          // },
        ],
      },
    ],
  },
];
